import { AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import {
  EsraPipingAPIClient,
  PressureTemperatureTableViewModel,
  PressureTemperatureCellViewModel,
  CommonNoteViewModel,
  PressureTemperatureTableNoteViewModel,
} from 'src/app/shared/models/autogenerated-piping';
import {
  faPlusCircle,
  faPen,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatTableDataSource } from '@angular/material/table';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { uniqueFilter } from 'src/app/shared/utils';
import { Constants } from 'src/app/shared/constants/constants';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PressureTempPreviewComponent } from '../pressure-temp-preview/pressure-temp-preview.component';
import { ConfirmDialogComponent } from '../../../../../shared/confirm-dialog/confirm-dialog.component';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';

@Component({
    selector: 'app-pressure-temp-edit',
    templateUrl: './pressure-temp-edit.component.html',
    styleUrls: ['./pressure-temp-edit.component.scss'],
    standalone: false
})
export class PressureTempEditComponent implements OnInit, AfterViewInit {
  @ViewChild('editorContent') editorContent?: ElementRef<HTMLElement>;

  constructor(
    private readonly dialogRef: MatDialogRef<PressureTempEditComponent, boolean>,
    private readonly esraApiClient: EsraPipingAPIClient,
    private readonly snackBarService: SnackBarService,
    private readonly formBuilder: FormBuilder,
    private readonly dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) private readonly pressureTemperatureTableData: PressureTemperatureTableViewModel | null
  ) {}

  static readonly ADDITIONAL_NOTE_TYPE = "0";
  static readonly TEST_NOTE_TYPE = "1";

  pressureTemperatureForm: FormGroup;

  faPlusCircle = faPlusCircle;
  faEdit = faPen;
  faTrashCan = faTrash;

  defaultPipeClassPressureUnits = Constants.imperialPipeClassPressureUnits;
  defaultPipeClassTemperatureUnits = Constants.imperialPipeClassTemperatureUnits;
  secondPipeClassPressureUnits: string = undefined;
  secondPipeClassTemperatureUnits: string = undefined;

  regexDigits = "^-?[0-9]*$";
  regexDigitsPositive = "^[0-9]*$";

  genericMissingValueMessage = "This is a required field";
  editTableDisplayedColumns: string[] = ['temperature', 'pressure'];
  readOnlyTableDisplayedColumns: string[] = ['temperatureF', 'pressurePSIG','temperatureC', 'pressureKPAG'];
  title: string;

  areCellsInMetric = false;
  validTableName = false;
  validTestNote = false;

  commonNotesAll: CommonNoteViewModel[];
  commonNotesSelected: CommonNoteViewModel[];
  commonNotesFiltered: CommonNoteViewModel[];

  commonNotesSelectedIds: string[];

  pressureTemperatureTable = new PressureTemperatureTableViewModel();
  updatedTable: PressureTemperatureTableViewModel;

  isMetric = false;
  isCreateMode = false;
  isScrolledFromTop: boolean = false;
  isScrolledToBottom: boolean = false;
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  addFormGroup: FormGroup;
  readOnlyDatasource: MatTableDataSource<{temperatureF: string, pressurePSIG: string, temperatureC: string, pressureKPAG: string}>;

  get pressureTemperatureCells(): FormArray {
    if (this.addFormGroup.get('pressureTemperatureCells')) {
      return this.addFormGroup.get('pressureTemperatureCells') as FormArray;
    }
  }

   ngOnInit(): void {
    this.pressureTemperatureTable = this.pressureTemperatureTableData
      ? PressureTemperatureTableViewModel.fromJS(this.pressureTemperatureTableData.toJSON())
      : new PressureTemperatureTableViewModel();
    this.isCreateMode = this.pressureTemperatureTableData === null;
    this.setTitle();
    this.initializeUpdatedTable();
    this.populateCommonNotes();
    this.initForm();
    if (this.pressureTemperatureTable.cells) {
      this.sortTableCellsByTemp(this.pressureTemperatureTable.cells);
    }
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      const editorContent: HTMLElement | undefined = this.editorContent?.nativeElement;
      if (editorContent) {
        this.updateScrollState(editorContent);
      }
    });
  }

  private setTitle() {
    if (this.isCreateMode) {
      this.title = 'Create a new pressure temperature table';
    } else {
      this.title = 'Edit pressure and temperature table';
    }
  }

  handleEditorScroll(event: Event): void {
    const editorContent: HTMLElement = event.target as HTMLElement;
    this.updateScrollState(editorContent);
  }

  private updateScrollState(editorContent: HTMLElement): void {
    const remainingScroll: number = editorContent.scrollHeight - editorContent.scrollTop - editorContent.clientHeight;
    this.isScrolledFromTop = editorContent.scrollTop > 8;
    this.isScrolledToBottom = remainingScroll <= 24;
  }

  private initForm() {
    let items = [];
    if (this.pressureTemperatureTable.cells && this.pressureTemperatureTable.cells.length > 0) {
      this.setupUnits();
      items = this.initializeEditTable(this.pressureTemperatureTable.cells);
      if (this.secondPipeClassPressureUnits !== undefined && this.secondPipeClassPressureUnits !== '') {
        this.readOnlyDatasource = this.initializeReadOnlyTable(this.pressureTemperatureTable.cells);
      }
    }

    this.addFormGroup = this.formBuilder.group({
      pressureTemperatureCells: this.formBuilder.array(items),
      tableName: new UntypedFormControl(this.pressureTemperatureTable.name, [
        Validators.required,
      ]),
      testNote: new UntypedFormControl(''),
      addNote: new UntypedFormControl(''),
    });

    if (this.pressureTemperatureTable.testNotes) {
      this.addFormGroup.get('testNote').setValue(this.pressureTemperatureTable.testNotes[0].note);
    }
    if (this.pressureTemperatureTable.additionalNotes) {
      this.addFormGroup.get('addNote').setValue(this.pressureTemperatureTable.additionalNotes[0].note);
    }
  }

  private setupUnits() {
    const pressureUnits = this.pressureTemperatureTable.cells.map(cell => cell.pressureUnit).filter(uniqueFilter);
    // Legacy check - We check if we have values for more than one unit pair
    if (pressureUnits.length > 1) {
      pressureUnits.splice(pressureUnits.indexOf(this.defaultPipeClassPressureUnits), 1);
      if (pressureUnits.length != 0) {
        this.secondPipeClassPressureUnits = pressureUnits[0];
        const temperatureUnits = this.pressureTemperatureTable.cells.map(cell => cell.temperatureUnit).filter(uniqueFilter);
        temperatureUnits.splice(pressureUnits.indexOf(this.defaultPipeClassTemperatureUnits), 1);
        this.secondPipeClassTemperatureUnits = temperatureUnits[0];
      }
    } else {
      if (this.defaultPipeClassPressureUnits != pressureUnits[0]) {
        this.areCellsInMetric = true;
      }
      this.defaultPipeClassPressureUnits = pressureUnits[0];
      this.defaultPipeClassTemperatureUnits = this.pressureTemperatureTable.cells.map(cell => cell.temperatureUnit).filter(uniqueFilter)[0];
    }
  }

  private initializeEditTable(cells: PressureTemperatureCellViewModel[] ): FormGroup[]{
    const ptTablesUnits = this.filterOnlySpecificPressureUnits(cells, this.defaultPipeClassPressureUnits);

    const formCells: FormGroup[]  = []

    for (const ptCell of ptTablesUnits) {
      const item = this.formBuilder.group({
        temperature: [ptCell.temperature, [Validators.required, Validators.pattern(this.regexDigits)]],
        pressure: [ptCell.pressure, [Validators.required, Validators.pattern(this.regexDigitsPositive)]],
      });
      formCells.push(item);
    }

    return formCells;
  }

  private initializeReadOnlyTable(cells: PressureTemperatureCellViewModel[] ): MatTableDataSource <{temperatureF: string, pressurePSIG:string, temperatureC:string, pressureKPAG: string}> {
    const ptTablesUnits1 = this.filterOnlySpecificPressureUnits(cells, this.defaultPipeClassPressureUnits);
    const ptTablesUnits2 = this.filterOnlySpecificPressureUnits(cells, this.secondPipeClassPressureUnits);

    const array = []

    for (let i = 0; i< ptTablesUnits1.length; i++) {
      const item = this.formBuilder.group({
        temperatureF: [ptTablesUnits1[i].temperature],
        pressurePSIG: [ptTablesUnits1[i].pressure],
        temperatureC: [ptTablesUnits2[i].temperature],
        pressureKPAG: [ptTablesUnits2[i].pressure],
      });
      array.push(item);
    }

    return new MatTableDataSource(array);
  }

  initializeUpdatedTable(): void {
    this.updatedTable = new PressureTemperatureTableViewModel(
      this.pressureTemperatureTable
    );
  }

  //Possibly to be extracted as pressure-temp-table.component has same methods
  filterOnlySpecificPressureUnits(cellsToFilter: PressureTemperatureCellViewModel[], unit: string): PressureTemperatureCellViewModel[] {
    return cellsToFilter.filter(cell => cell.pressureUnit.toLowerCase() == unit.toLowerCase());
  }

  filterOnlySpecificTemperatureUnits(cellsToFilter: PressureTemperatureCellViewModel[], unit: string): PressureTemperatureCellViewModel[] {
    return cellsToFilter.filter(cell => cell.temperatureUnit.toLowerCase() == unit.toLowerCase());
  }

  /**
   * This function sorts all cells in each pressureTemperatureTable in ascending order
   */
  private sortTableCellsByTemp(tableToSort: PressureTemperatureCellViewModel[] ): void {
    tableToSort.sort((a, b) => {
      if (a.temperature > b.temperature) {
        return 1;
      }
      if (a.temperature < b.temperature) {
        return -1;
      }
      return 0;
    });
  }

  setMetricBool(event: MatSlideToggleChange): void {
    this.areCellsInMetric = event.checked;
    this.snackBarService.showWarning('You are changing units remember to recalculate pressure and temperature values');
  }

  populateCommonNotes(): void {
    if (this.pressureTemperatureTable?.commonNotes) {
      this.commonNotesSelected = this.pressureTemperatureTable.commonNotes;
      this.commonNotesSelectedIds = this.pressureTemperatureTable.commonNotes
        .map(cn => cn.noteId);
    } else {
      this.commonNotesSelected = [];
      this.commonNotesSelectedIds = [];
    }

    this.esraApiClient.getAllCurrentCommonNotes().subscribe((response) => {
      // Since there are multiple CommonNotes with the same Code, the idea is to sort by ID and distinct
      // so distinct items have same NoteID so we will be using same instance of CommonNote
      response.sort((a, b) => {
        if (a.noteId > b.noteId) {
          return 1;
        }
        if (a.noteId < b.noteId) {
          return -1;
        }
        return 0;
      });

      response = [...new Map(response.map(item =>
        [item['codeId'], item])).values()];

      const commonNotesWithLetters: CommonNoteViewModel[] = [];
      const commonNotesWithoutLetters: CommonNoteViewModel[] = [];

      response.forEach((cn => {
        if (this.stringContainsLetter(cn.codeId)) {
          commonNotesWithLetters.push(cn);
        } else {
          commonNotesWithoutLetters.push(cn);
        }
      }));

      commonNotesWithLetters.sort((a, b) => {
        if (a.codeId > b.codeId) {
          return 1;
        }
        if (a.codeId < b.codeId) {
          return -1;
        }
        return 0;
      });

      commonNotesWithoutLetters.sort((a, b) => {
        return parseInt(a.codeId) - parseInt(b.codeId);
      });

      const sortedResponse = commonNotesWithLetters.concat(commonNotesWithoutLetters);
      this.commonNotesAll = sortedResponse;
      this.commonNotesFiltered = this.commonNotesAll;
    });
  }

  stringContainsLetter(str: string): boolean {
    return /[a-zA-Z]/.test(str)
  }

  onKeyCommonNotes(event): void {
    this.commonNotesFiltered = this.searchCommonNotesAll(event);
  }

  searchCommonNotesAll(value: string): CommonNoteViewModel[] {
    const filter = value.toLowerCase();
    return this.commonNotesAll.filter(
      (option) =>
        option.note.toLowerCase().includes(filter) ||
        option.codeId.toLowerCase().startsWith(filter)
    );
  }

  selectCommonNotes(event) {
    const eventValueHolder: string | undefined = String(event.value);
    const selectedNotesGUIDArr = eventValueHolder.split(',');

    this.commonNotesSelected = this.commonNotesSelected.filter(commonNote => selectedNotesGUIDArr.includes(commonNote.noteId));
    const commonNotesSelectedIds = this.commonNotesSelected.map(commonNote => commonNote.noteId);

    selectedNotesGUIDArr.filter(guid =>  !commonNotesSelectedIds.includes(guid))
      .map(guid => this.getCommonNoteVMById(guid))
      .filter(commonNote => commonNote !== undefined && commonNote !== null)
      .forEach(commonNote => this.commonNotesSelected.push(commonNote));
}

  getCommonNoteVMById(id: string): CommonNoteViewModel {
    const filteredArr = this.commonNotesAll.filter(
      (option) => option.noteId === id
    );
    return filteredArr[0];
  }

  removeSelectedNote(note: CommonNoteViewModel): void {
    this.commonNotesSelected.forEach((item, index) => {
      if (item.noteId === note.noteId) {
        this.commonNotesSelected.splice(index, 1);
      }
    });
    this.commonNotesSelectedIds = this.commonNotesSelectedIds.filter(noteId => noteId != note.noteId);
  }

  updateTable(): void {
    this.pressureTemperatureTable.name = this.addFormGroup?.get('tableName')?.value;
    this.pressureTemperatureTable.commonNotes = this.commonNotesSelected;
    this.pressureTemperatureTable.additionalNotes = this.getAdditionalNote();
    this.pressureTemperatureTable.testNotes = this.getTestNote();
    this.addCellsToTable();
  }

  saveTable(): void{
    if (this.addFormGroup.invalid) {
      this.addFormGroup.markAllAsTouched();
      this.snackBarService.showSnackBar(true, 'Please correct errors in selected fields.');
      return;
    }

    this.updateTable();

    if (this.pressureTemperatureTable.cells.length <= 0) {
      const dialogData = {
        'title': 'Confirm',
        'dialogMessage':'Do you want to save this table without any Pressure or Temperature data?'
      };

      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        maxWidth: "400px",
        data: dialogData
      });

      dialogRef.afterClosed().subscribe(dialogResult => {
        if(dialogResult && dialogResult === true) {
          this.processPersisting();
        }
      });
    } else {
      this.processPersisting();
    }

    if (this.addFormGroup.invalid) {
      this.addFormGroup.markAllAsTouched();
    }
  }

  private processPersisting() {
    if (this.addFormGroup.valid && this.isCreateMode) {
      this.esraApiClient.createPressureTemperatureTable(this.pressureTemperatureTable).pipe(
        indicate(this.saving$),
        first()
      )
        .subscribe({
          next: () => {
            this.snackBarService.showSnackBar(
              true,
              `The pressure temperature table ${this.pressureTemperatureTable.name} was successfully saved`
            );
            this.dialogRef.close(true);
          },
          error: (err) => {
            const message = err.response ? err.response : err.message;
            this.snackBarService.showError(
              `Error occured: ${message}`
            );
          }
        });
    }

    if (this.addFormGroup.valid && !this.isCreateMode) {
      this.esraApiClient.updatePressureTemperatureTable(this.pressureTemperatureTable).pipe(
        indicate(this.saving$),
        first()
      )
        .subscribe({
          next: () => {
            // Notify any open revision tabs that this PTT was updated so they can
            // re-validate and enable the sync button without requiring a page refresh.
            const channel: BroadcastChannel = new BroadcastChannel('ptt-dictionary-updated');
            channel.postMessage({ tableId: this.pressureTemperatureTable.id, tableName: this.pressureTemperatureTable.name });
            channel.close();
            this.snackBarService.showSnackBar(
              true,
              `The pressure temperature table ${this.pressureTemperatureTable.name} was successfully updated`
            );
            this.dialogRef.close(true);
          },
          error: (err) => {
            const message = err.response ? err.response : err.message;
            this.snackBarService.showError(
              `Error occured: ${message}`
            );
          }
        });
    }
  }

  addRowToTable(): void {
    const item = this.formBuilder.group({
      temperature: ["", [Validators.required, Validators.pattern(this.regexDigits)]],
      pressure: ["", [Validators.required, Validators.pattern(this.regexDigitsPositive)]],
    });
    this.pressureTemperatureCells.push(item);
  }

  removeRowFromTable(index: number): void {
    this.pressureTemperatureCells.removeAt(index);
  }

  addCellsToTable(): void {
    const newCellArr = [];
    for (const cell of this.pressureTemperatureCells.controls) {
      const newCell = new PressureTemperatureCellViewModel();
      newCell.pressure = cell?.get('pressure')?.value;
      newCell.temperature = cell?.get('temperature')?.value;
      newCell.temperatureUnit = this.areCellsInMetric ? Constants.metricPipeClassTemperatureUnits : Constants.imperialPipeClassTemperatureUnits;
      newCell.pressureUnit = this.areCellsInMetric ? Constants.metricPipeClassPressureUnits : Constants.imperialPipeClassPressureUnits;
      newCellArr.push(newCell);
    }
    this.pressureTemperatureTable.cells = newCellArr;
  }

  getAdditionalNote(): PressureTemperatureTableNoteViewModel[] {
    const returnArr = [];
    const pttableAdditionalNote = new PressureTemperatureTableNoteViewModel();
    if (this.addFormGroup?.get('addNote')?.value != null) {
      pttableAdditionalNote.note =
        this.addFormGroup?.get('addNote')?.value;
      pttableAdditionalNote.type = PressureTempEditComponent.ADDITIONAL_NOTE_TYPE;
      returnArr.push(pttableAdditionalNote);
      return returnArr;
    }
  }

  getTestNote(): PressureTemperatureTableNoteViewModel[] {
    const returnArr = [];
    const pttableTestNote = new PressureTemperatureTableNoteViewModel();
    if (this.addFormGroup?.get('testNote')?.value != null) {
      pttableTestNote.note =
        this.addFormGroup?.get('testNote')?.value;
      pttableTestNote.type = PressureTempEditComponent.TEST_NOTE_TYPE;
      returnArr.push(pttableTestNote);
      return returnArr;
    }
  }

  previewTable() {
    this.updateTable();
    this.dialog.open(PressureTempPreviewComponent, {
      minWidth: '60%',
      minHeight: '50%',
      maxWidth: '90%',
      maxHeight: '90%',
      data: this.pressureTemperatureTable
    })
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }
}
